import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { MaskLine } from "@/components/site/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1622547748225-3fc4abd2cca0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNkJTIwZ2VvbWV0cmljJTIwc2hhcGVzJTIwbWluaW1hbHxlbnwwfHx8fDE3ODM4Nzk0MDh8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onCta, onPlanes }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBlobA = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yBlobB = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-brand-bgAlt pt-36 pb-28 sm:pt-40 sm:pb-32"
      data-testid="hero-section"
    >
      {/* Parallax abstract layers */}
      <motion.div
        style={{ y: yBlobA }}
        className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-brand-indigo/10 blur-3xl"
      />
      <motion.div
        style={{ y: yBlobB }}
        className="pointer-events-none absolute right-[-60px] bottom-8 h-80 w-80 rounded-full bg-brand-mint/15 blur-3xl"
      />
      <motion.div
        style={{ scale: scaleImg, opacity }}
        className="pointer-events-none absolute right-0 top-10 hidden lg:block w-[420px] h-[420px] opacity-40"
      >
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="h-full w-full rounded-[28px] object-cover mix-blend-multiply"
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full bg-brand-mint/15 px-4 py-1.5 text-[13px] font-medium text-[#085041]"
          data-testid="hero-pill"
        >
          <Check size={15} strokeWidth={2.5} className="text-brand-mint" />
          Planes 100% personalizables · Sin contratos de permanencia
        </motion.span>

        <h1 className="mt-6 text-4xl sm:text-6xl md:text-7xl font-medium leading-[1.05] tracking-tight text-brand-ink">
          <MaskLine delay={0.1}>Tu secretaria virtual,</MaskLine>
          <MaskLine delay={0.24} className="text-brand-indigo -mt-[0.14em]">
            sin oficina ni nóminas
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mx-auto mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#5F5E5A]"
        >
          Agenda, llamadas, facturación y hasta tus viajes de trabajo: nos ocupamos de
          todo lo que te roba tiempo. Para pymes y autónomos.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.64 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-3.5"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={onCta}
            className="group inline-flex items-center gap-2 rounded-[10px] bg-brand-indigo px-7 py-3.5 text-[15px] font-medium text-white shadow-[0_8px_24px_rgba(79,70,229,0.32)] hover:-translate-y-0.5 transition-transform duration-200"
          >
            Pide tu propuesta
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-ghost"
            onClick={onPlanes}
            className="rounded-[10px] border border-[#D3D1C7] bg-white px-7 py-3.5 text-[15px] font-medium text-brand-ink hover:-translate-y-0.5 transition-transform duration-200"
          >
            Ver planes
          </button>
        </motion.div>
      </div>
    </section>
  );
}
